import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SirenLookup from '@/components/SirenLookup';

const contactSchema = z.object({
  categorie: z.string().min(1, 'Catégorie requise'),
  entreprise: z.string().optional(),
  siret: z.string().optional().refine(
    (v) => !v || /^\d{9}$|^\d{14}$/.test(v.replace(/\s/g, '')),
    { message: 'SIRET = 14 chiffres (ou SIREN = 9)' }
  ),
  nom_prenom: z.string().min(1, 'Nom du contact requis'),
  telephone: z.string().min(1, 'Téléphone requis'),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  site_web: z.string().optional(),
  adresse: z.string().optional(),
  ville: z.string().optional(),
  code_postal: z.string().optional(),
  notes: z.string().optional(),
});

export default function ContactForm({ contact, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: contact || { categorie: '' },
  });

  const categorieValue = watch('categorie');

  useEffect(() => {
    api.get('/contacts/categories')
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (contact) {
        await api.put(`/contacts/${contact.contact_id}`, data);
      } else {
        await api.post('/contacts', data);
      }
      onSuccess();
    } catch (error) {
      console.error('Erreur sauvegarde contact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSirenSelect = (company) => {
    if (company.nom) setValue('entreprise', company.nom, { shouldValidate: true });
    if (company.siret) setValue('siret', company.siret, { shouldValidate: true });
    if (company.adresse) setValue('adresse', company.adresse);
    if (company.code_postal) setValue('code_postal', company.code_postal);
    if (company.ville) setValue('ville', company.ville);
    if (company.representant_nom) setValue('nom_prenom', company.representant_nom, { shouldValidate: true });
    // Stash extra legal info in the notes field for traceability (SIRET reste structuré ailleurs)
    const legalInfo = [
      company.forme_juridique_libelle && `Forme : ${company.forme_juridique_libelle}`,
      company.capital_social && `Capital : ${company.capital_social} €`,
      company.numero_rcs && `RCS : ${company.numero_rcs}`,
      company.tva_intracom && `TVA : ${company.tva_intracom}`,
    ].filter(Boolean).join(' · ');
    if (legalInfo) {
      const existing = (watch('notes') || '').trim();
      setValue('notes', existing ? `${existing}\n\n${legalInfo}` : legalInfo);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {!contact && (
        <SirenLookup onSelect={handleSirenSelect} placeholder="SIREN, SIRET ou nom du client" />
      )}
      <div>
        <Label htmlFor="categorie">Catégorie *</Label>
        <Select value={categorieValue} onValueChange={(value) => setValue('categorie', value, { shouldValidate: true })}>
          <SelectTrigger data-testid="contact-categorie-select">
            <SelectValue placeholder="Choisir une catégorie" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.categorie && <p className="text-sm text-red-600 mt-1">{errors.categorie.message}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="entreprise">Entreprise</Label>
          <Input id="entreprise" {...register('entreprise')} placeholder="Raison sociale" data-testid="contact-entreprise-input" />
        </div>
        <div>
          <Label htmlFor="nom_prenom">Contact principal *</Label>
          <Input id="nom_prenom" {...register('nom_prenom')} placeholder="Nom & prénom" data-testid="contact-nom-input" />
          {errors.nom_prenom && <p className="text-sm text-red-600 mt-1">{errors.nom_prenom.message}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="siret">SIRET (clients professionnels)</Label>
        <Input
          id="siret"
          {...register('siret')}
          placeholder="14 chiffres — ex : 73282932000074"
          data-testid="contact-siret-input"
          inputMode="numeric"
        />
        {errors.siret && <p className="text-sm text-red-600 mt-1">{errors.siret.message}</p>}
        <p className="text-[11px] text-slate-500 mt-1">Obligatoire pour les factures Chorus Pro et la création client Pennylane.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="telephone">Téléphone *</Label>
          <Input id="telephone" {...register('telephone')} data-testid="contact-telephone-input" />
          {errors.telephone && <p className="text-sm text-red-600 mt-1">{errors.telephone.message}</p>}
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register('email')} data-testid="contact-email-input" />
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="site_web">Site web</Label>
        <Input id="site_web" {...register('site_web')} placeholder="https://..." data-testid="contact-siteweb-input" />
      </div>

      <div>
        <Label htmlFor="adresse">Adresse</Label>
        <Input id="adresse" {...register('adresse')} data-testid="contact-adresse-input" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ville">Ville</Label>
          <Input id="ville" {...register('ville')} data-testid="contact-ville-input" />
        </div>
        <div>
          <Label htmlFor="code_postal">Code Postal</Label>
          <Input id="code_postal" {...register('code_postal')} data-testid="contact-cp-input" />
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea id="notes" {...register('notes')} rows={3}
          placeholder="Tarifs, conditions, heures d'ouverture, informations utiles..."
          data-testid="contact-notes-input" />
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} data-testid="submit-contact-btn">
          {loading ? 'Enregistrement...' : contact ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}
